import React, { useState, useEffect } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Brain, AlertTriangle, CheckCircle2, TrendingUp, Shield, Clock, Loader2 } from 'lucide-react';

const RISK_SCORE_COLORS = {
  1: 'text-green-600', 2: 'text-green-600', 3: 'text-green-500',
  4: 'text-amber-500', 5: 'text-amber-500', 6: 'text-amber-600',
  7: 'text-orange-600', 8: 'text-red-500', 9: 'text-red-600', 10: 'text-red-700',
};

export default function AIAnalysisPanel({ matterId }) {
  const [analysis, setAnalysis] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.disputes.getAIAnalysis(matterId);
        if (res.data?.analysis) {
          setAnalysis(res.data.analysis);
          setGeneratedAt(res.data.generated_at);
        }
      } catch { /* silent */ } finally { setLoading(false); }
    })();
  }, [matterId]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.disputes.runAIAnalysis(matterId);
      setAnalysis(res.data.analysis);
      setGeneratedAt(res.data.generated_at);
      toast.success('AI analysis complete');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI analysis failed');
    } finally { setGenerating(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin h-6 w-6 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  return (
    <div className="space-y-4" data-testid="ai-analysis-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><Brain className="h-4 w-4" /> AI Dispute Analysis</h3>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1 text-xs" onClick={handleGenerate} disabled={generating} data-testid="run-ai-analysis-btn">
          {generating ? <><Loader2 className="h-3 w-3 animate-spin" /> Analysing...</> : <><Brain className="h-3 w-3" /> {analysis ? 'Re-Analyse' : 'Run Analysis'}</>}
        </Button>
      </div>

      {!analysis ? (
        <Card><CardContent className="p-8 text-center">
          <Brain className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No AI analysis available</p>
          <p className="text-xs text-slate-400 mt-1">Click "Run Analysis" to get AI-powered insights on this dispute</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {generatedAt && <p className="text-[10px] text-slate-400">Generated: {new Date(generatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>}

          {/* Risk Score */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className={`text-4xl font-bold ${RISK_SCORE_COLORS[analysis.risk_score] || 'text-slate-600'}`}>{analysis.risk_score}</p>
                  <p className="text-[10px] uppercase text-slate-500 mt-0.5">Risk Score</p>
                </div>
                <div className="flex-1">
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${analysis.risk_score <= 3 ? 'bg-green-500' : analysis.risk_score <= 6 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${analysis.risk_score * 10}%` }} />
                  </div>
                  <p className="text-xs text-slate-600 mt-2">{analysis.risk_assessment}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Recommended Steps */}
            <Card>
              <CardContent className="p-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> Recommended Next Steps</h4>
                <div className="space-y-1.5">
                  {(analysis.recommended_next_steps || []).map((step, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-slate-700">
                      <span className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">{i + 1}</span>
                      <span>{step}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Time-Bar Warnings */}
            <Card>
              <CardContent className="p-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Time-Bar Warnings</h4>
                {(analysis.time_bar_warnings || []).length === 0 ? (
                  <p className="text-xs text-green-600">No time-bar concerns identified</p>
                ) : (
                  <div className="space-y-1.5">
                    {analysis.time_bar_warnings.map((w, i) => (
                      <div key={i} className="flex items-start gap-2 p-1.5 bg-red-50 rounded text-xs text-red-700">
                        <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /><span>{w}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Strengths */}
            <Card>
              <CardContent className="p-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-green-600 mb-2 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Strengths</h4>
                <div className="space-y-1">
                  {(analysis.strengths || []).map((s, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-slate-700"><CheckCircle2 className="h-3 w-3 text-green-500 mt-0.5 shrink-0" /><span>{s}</span></div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Weaknesses */}
            <Card>
              <CardContent className="p-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-red-600 mb-2 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Weaknesses</h4>
                <div className="space-y-1">
                  {(analysis.weaknesses || []).map((w, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-slate-700"><AlertTriangle className="h-3 w-3 text-red-400 mt-0.5 shrink-0" /><span>{w}</span></div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Procedural Guidance */}
          <Card>
            <CardContent className="p-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><Shield className="h-3.5 w-3.5" /> Procedural Guidance</h4>
              <p className="text-xs text-slate-700">{analysis.procedural_guidance}</p>
            </CardContent>
          </Card>

          {/* Settlement Recommendation */}
          {analysis.settlement_recommendation && (
            <Card className="border-amber-200">
              <CardContent className="p-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-amber-600 mb-2">Settlement Strategy</h4>
                <p className="text-xs text-slate-700">{analysis.settlement_recommendation}</p>
              </CardContent>
            </Card>
          )}

          {/* Key Issues Summary */}
          {analysis.key_issues_summary && (
            <Card>
              <CardContent className="p-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Key Issues Summary</h4>
                <p className="text-xs text-slate-700">{analysis.key_issues_summary}</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
